import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import {
    useForm,
    FormProvider,
    SubmitHandler,
    SubmitErrorHandler,
    Controller,
} from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import Select from "react-select";

import { IPhotoUploadFormData } from "../../types/forms";
import FileUpload from "../../components/FileUpload";
import { MAX_FILE_SIZE, VALID_FILE_EXTENSIONS } from "../../lib/globals";
import "../../assets/css/majorybl/pages/contact.scss";
import { displayFormAlert } from "../../lib/utils";
import {
    globalActions,
    selectAgeLevels,
} from "../../features/global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";
import { uploadPhotos } from "../../lib/api";

const PhotoUpload = (): React.JSX.Element => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(globalActions.getAgeLevels());
    }, []);
    const ageLevels = useAppSelector(selectAgeLevels);

    const allowedExtensions = VALID_FILE_EXTENSIONS.image.slice();
    const [fileList, setFileList] = useState<File[]>([]);

    const recaptcha = useRef<ReCAPTCHA>(null);

    const verifyCaptcha = (): boolean => {
        const recaptchaValue = recaptcha.current?.getValue();
        if (!recaptchaValue) {
            methods.setError("recaptcha", {
                type: "custom",
                message: "Please verify the reCAPTCHA!",
            });
            return false;
        } else {
            methods.clearErrors("recaptcha");
            return true;
        }
    };

    const verifyPhotos = (): boolean => {
        if (fileList.length === 0) {
            methods.setError("files", {
                type: "custom",
                message: "Please upload at least 1 photo",
            });
            return false;
        } else {
            methods.clearErrors("files");
            return true;
        }
    };

    const methods = useForm<IPhotoUploadFormData>({
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<IPhotoUploadFormData> = async data => {
        if (verifyCaptcha() && verifyPhotos()) {
            setIsSubmitting(true);
            data.files = fileList;

            await uploadPhotos(data)
                .then(response => {
                    dispatch(
                        alertActions.success({
                            title: "Photos uploaded successfully",
                            text: "Upon approval, they will be added to our gallery.",
                            dismissible: true,
                        }),
                    );

                    setFileList([]);
                    methods.resetField("ageLevel", {
                        defaultValue: { value: 0, label: "" },
                    });
                    methods.reset();
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem uploading the photos.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    };

    const onError: SubmitErrorHandler<IPhotoUploadFormData> = (): void => {
        verifyCaptcha();
        verifyPhotos();
    };

    const onChangeRecaptcha = async (token: string | null): Promise<void> => {
        const recaptchaValue = recaptcha.current?.getValue();
        methods.setValue("recaptcha", recaptchaValue);
        verifyCaptcha();
        // TODO: Verify response by sending token to back-end API
    };

    return (
        <div data-testid="photoUploadContent">
            <Container className="py-5">
                <h4>
                    We would love for you to share your photos. Action photos,
                    fun photos or team photos, show off your MAJOR YBL Players.
                </h4>
                <p>
                    Submit up to 5 images to be displayed on the MAJOR YBL
                    website!
                </p>
                <FormProvider {...methods}>
                    <Form
                        onSubmit={methods.handleSubmit(onSubmit, onError)}
                        id="photoUploadForm"
                    >
                        <Row>
                            <Form.Label>Name *</Form.Label>
                            <Form.Group as={Col} controlId="firstName">
                                <Form.Control
                                    type="text"
                                    {...methods.register("firstName", {
                                        required: "First Name is required",
                                    })}
                                    aria-invalid={
                                        methods.formState.errors.firstName
                                            ? "true"
                                            : "false"
                                    }
                                />
                                <Form.Label>First</Form.Label>
                                {methods.formState.errors.firstName &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.firstName
                                            .message as string,
                                    })}
                            </Form.Group>
                            <Form.Group as={Col} controlId="lastName">
                                <Form.Control
                                    type="text"
                                    {...methods.register("lastName", {
                                        required: "Last Name is required",
                                    })}
                                    aria-invalid={
                                        methods.formState.errors.lastName
                                            ? "true"
                                            : "false"
                                    }
                                />
                                <Form.Label>Last</Form.Label>
                                {methods.formState.errors.lastName &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.lastName
                                            .message as string,
                                    })}
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} md={6} controlId="teamName">
                                <Form.Label>Team Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    {...methods.register("teamName", {
                                        required: "Team Name is required",
                                    })}
                                    aria-invalid={
                                        methods.formState.errors.teamName
                                            ? "true"
                                            : "false"
                                    }
                                />
                                {methods.formState.errors.teamName &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.teamName
                                            .message as string,
                                    })}
                            </Form.Group>
                            <Form.Group controlId="ageLevel" as={Col} md={6}>
                                <Form.Label>Age Level *</Form.Label>
                                <Controller
                                    name="ageLevel"
                                    control={methods.control}
                                    rules={{
                                        required:
                                            "Please select the team's age level",
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={true}
                                            options={ageLevels.map(a => {
                                                return {
                                                    value: parseInt(
                                                        a.id.toString(),
                                                    ),
                                                    label: a.ageLevel,
                                                };
                                            })}
                                        />
                                    )}
                                />
                                {methods.formState.errors.ageLevel &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.ageLevel
                                            .message as string,
                                    })}
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} xs={6} controlId="email">
                                <Form.Label>Email *</Form.Label>
                                <Form.Control
                                    type="email"
                                    {...methods.register("email", {
                                        required: "Email address  is required",
                                        pattern: {
                                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                            message: "Email is invalid",
                                        },
                                    })}
                                    aria-invalid={
                                        methods.formState.errors.email
                                            ? "true"
                                            : "false"
                                    }
                                />
                                <Form.Label>Enter Email</Form.Label>
                                {methods.formState.errors.email &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.email
                                            .message as string,
                                    })}
                            </Form.Group>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Label>Select up to 5 images</Form.Label>
                                <Form.Group controlId="photos">
                                    <FileUpload
                                        maxFileCount={5}
                                        allowedFileExtensions={
                                            allowedExtensions
                                        }
                                        maxFileSize={MAX_FILE_SIZE}
                                        fileList={fileList}
                                        setFileList={setFileList}
                                    />
                                    <Form.Label>
                                        Accepted file types: jpg, png, gif
                                        <br />
                                        Max file size: 8 MB, Max Files: 5
                                    </Form.Label>
                                </Form.Group>
                                {methods.formState.errors.files &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.files
                                            .message as string,
                                    })}
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={3}>
                                <Form.Label>CAPTCHA</Form.Label>
                                <ReCAPTCHA
                                    ref={recaptcha}
                                    sitekey={
                                        process.env.RECAPTCHA_SITE_KEY as string
                                    }
                                    onChange={onChangeRecaptcha}
                                />
                                {methods.formState.errors.recaptcha &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: methods.formState.errors.recaptcha
                                            .message as string,
                                    })}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </FormProvider>
            </Container>
        </div>
    );
};

export default PhotoUpload;
